import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Directive,
  TemplateRef,
  computed,
  contentChildren,
  inject,
  input,
} from '@angular/core';

/** Una colonna della tabella. */
export interface TableColumn {
  /** Chiave della colonna: il valore di default è `row[key]`, oppure il modello `bsTableCell`. */
  readonly key: string;
  /** Intestazione italiana; per la colonna delle azioni resta per i lettori di schermo. */
  readonly label: string;
  /** Colonna delle azioni: allineata a destra, larga quanto il contenuto, intestazione nascosta. */
  readonly actions?: boolean;
  /** Celle senza a capo, per stati, pillole e date. */
  readonly nowrap?: boolean;
  /** Valori in monospazio (codici, date, misure). */
  readonly mono?: boolean;
  readonly align?: 'start' | 'end';
}

/** Contesto del modello di cella: `let-row` e `let-index="index"`. */
export interface TableCellContext<T = unknown> {
  readonly $implicit: T;
  readonly index: number;
}

/** Modello di una cella: `<ng-template bsTableCell="stato" let-row>…</ng-template>`. */
@Directive({ selector: 'ng-template[bsTableCell]' })
export class TableCellDirective {
  readonly key = input.required<string>({ alias: 'bsTableCell' });
  readonly template = inject<TemplateRef<TableCellContext>>(TemplateRef);
}

/**
 * Tabella della libreria (tavola 3A): intestazioni vere (`th scope="col"`), colonna delle azioni
 * allineata a destra e larga quanto il suo contenuto, stati e pillole senza a capo. Su telefono la
 * tabella scorre in orizzontale dentro un'area raggiungibile da tastiera.
 */
@Component({
  selector: 'bs-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet],
  host: { class: 'block' },
  template: `
    <div
      class="overflow-x-auto rounded-2xl border border-stone-200 bg-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink dark:border-stone-800 dark:bg-stone-900 dark:focus-visible:outline-bsite-400"
      role="region"
      tabindex="0"
      [attr.aria-label]="caption()"
      [attr.data-testid]="testId()"
    >
      <table class="w-full border-collapse text-left text-sm">
        <caption class="sr-only">
          {{
            caption()
          }}
        </caption>
        <thead class="bg-stone-50 dark:bg-stone-950">
          <tr>
            @for (column of columns(); track column.key) {
              <th scope="col" [class]="headerClass(column)">
                <span [class.sr-only]="column.actions">{{ column.label }}</span>
              </th>
            }
          </tr>
        </thead>
        <tbody class="divide-y divide-stone-200 dark:divide-stone-800">
          @for (row of rows(); track trackRow(row, index); let index = $index) {
            <tr [attr.data-testid]="testId() ? testId() + '-row' : null">
              @for (column of columns(); track column.key) {
                <td [class]="cellClass(column)">
                  @if (templates().get(column.key); as template) {
                    <ng-container
                      [ngTemplateOutlet]="template"
                      [ngTemplateOutletContext]="{ $implicit: row, index }"
                    />
                  } @else {
                    {{ cellValue(row, column.key) }}
                  }
                </td>
              }
            </tr>
          } @empty {
            <tr>
              <td
                [attr.colspan]="columns().length"
                class="px-4 py-6 text-center text-stone-600 dark:text-stone-300"
              >
                {{ emptyText() }}
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  `,
})
export class TableComponent<T = unknown> {
  readonly columns = input.required<readonly TableColumn[]>();
  readonly rows = input.required<readonly T[]>();
  /** Nome della tabella per i lettori di schermo (e dell'area che scorre). */
  readonly caption = input.required<string>();
  /** Campo che identifica la riga, per mantenere lo stato degli elementi fra un aggiornamento e l'altro. */
  readonly rowKey = input<string | null>(null);
  readonly emptyText = input('Nessun elemento.');
  readonly testId = input<string | null>(null);

  private readonly cells = contentChildren(TableCellDirective);
  protected readonly templates = computed(
    () => new Map(this.cells().map((cell) => [cell.key(), cell.template])),
  );

  protected headerClass(column: TableColumn): string {
    const align = column.actions || column.align === 'end' ? 'text-right' : 'text-left';
    const width = column.actions ? 'w-px' : '';
    return `whitespace-nowrap px-4 py-3 text-xs font-semibold text-stone-600 dark:text-stone-300 ${align} ${width}`;
  }

  protected cellClass(column: TableColumn): string {
    const classes = ['px-4 py-3 align-middle text-ink dark:text-stone-100'];
    if (column.actions) {
      classes.push('w-px whitespace-nowrap text-right');
    } else if (column.align === 'end') {
      classes.push('text-right');
    }
    if (column.nowrap) {
      classes.push('whitespace-nowrap');
    }
    if (column.mono) {
      classes.push('font-mono');
    }
    return classes.join(' ');
  }

  protected cellValue(row: T, key: string): string {
    const value = row && typeof row === 'object' ? (row as Record<string, unknown>)[key] : null;
    return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  }

  protected trackRow(row: T, index: number): unknown {
    const key = this.rowKey();
    return key && row && typeof row === 'object' ? (row as Record<string, unknown>)[key] : index;
  }
}
